import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { listInquiries, deleteInquiry, type InquiryData } from "@/services/inquiry-service";
import { ConfirmDialog, Pagination } from "@/components/common";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import icArrowLeft from "@/assets/icons/ic-arrow-left.svg";
import { formatDate } from "@/lib/utils";
import IconHelp from "@/assets/icons/icon-help.svg";

const ITEMS_PER_PAGE = 10;

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; padding: string }> =
  {
    completed: {
      label: "답변완료",
      bg: "bg-[#43946C]",
      text: "text-[#000000]",
      padding: "px-[8px]",
    },
    in_progress: {
      label: "처리 중",
      bg: "bg-[#FFAA72]",
      text: "text-[#000000]",
      padding: "px-[12.5px]",
    },
  };

export default function InquiryListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [inquiries, setInquiries] = useState<InquiryData[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const fetchInquiries = useCallback(async (page: number) => {
    setIsLoading(true);
    try {
      const result = await listInquiries(page, ITEMS_PER_PAGE);
      setInquiries(result.items);
      setTotal(result.total);
    } catch {
      setInquiries([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInquiries(currentPage);
  }, [currentPage, fetchInquiries]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteInquiry(deleteTarget);
      toast({ description: "문의가 삭제되었습니다." });
      setDeleteTarget(null);
      if (total <= 1) {
        navigate("/my-page");
      } else if (inquiries.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchInquiries(currentPage);
      }
    } catch {
      setDeleteTarget(null);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.in_progress;
    return (
      <div
        className={`inline-flex shrink-0 items-center gap-1 rounded ${config.padding} w-fit h-6 ${config.bg} ${config.text}`}
      >
        <p className="text-sm leading-5 tracking-[-0.01em]">{config.label}</p>
      </div>
    );
  };

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div 
        className="flex flex-col gap-7 lg:gap-6 px-5 py-6 lg:px-[160px] lg:py-10 max-w-[1440px] mx-auto w-full"
        style={{ display: isLoading ? "none" : "flex" }}
      >
      {/* Header */}
      <div className="flex flex-col gap-4 w-full lg:flex-row lg:items-center">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/my-page")}
          className="w-12 h-12"
        >
          <img src={icArrowLeft} alt="Back" />
        </Button>
        <h1 className="flex-1 text-xl font-bold leading-7 tracking-[-0.02em] text-[#202020] whitespace-pre-line break-all">
          {user?.requester_name || "User"}님의 문의 사항
        </h1>
      </div>

      {/* Mobile: Card layout */}
          <div className="flex flex-col gap-6 lg:hidden rounded border-t border-[#00000027] pt-7">
            {inquiries.length === 0 ? (
              <p className="text-base text-[#8D8D8D] p-4">문의 내역이 없습니다.</p>
            ) : (
              inquiries.map((inquiry) => (
                <div
                  key={inquiry.id}
                  className="overflow-hidden cursor-pointer rounded border border-black/[0.09] "
                  onClick={() => navigate(`/inquiries/${inquiry.id}`)}
                >
                  <div className="px-3 py-3 pb-0 flex justify-between">
                    <p
                      className="block truncate text-base font-bold text-[rgba(0,0,0,0.91)] max-w-[232px]"
                      title={inquiry.title}
                    >
                      {inquiry.title}
                    </p>
                    {getStatusBadge(inquiry.status)}
                  </div>
                  <p className="text-base text-[#202020] px-3">{formatDate(inquiry.created_at)}</p>
                  <div className="p-3  space-y-4">
                    <div className="h-px bg-[#00000027]" />
                    <div
                      className="flex justify-end items-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        onClick={() => setDeleteTarget(inquiry.id)}
                        className="w-full h-8 rounded bg-[#F52B0018]  py-3 text-xs font-bold text-black  transition-colors"
                      >
                        삭제
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>

          {/* Desktop: Table layout */}
          <div className="hidden lg:block w-full rounded-none border border-black/[0.09] overflow-hidden">
            <Table>
              <TableHeader className="[&_tr]:border-b-0">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="bg-black/[0.04] text-xs font-normal uppercase text-[#000000] h-10 py-0 px-3 border-b border-black/[0.09]">
                    제목
                  </TableHead>
                  <TableHead className="bg-black/[0.04] text-xs font-normal uppercase text-[#000000] h-10 py-0 px-3 w-[339px] border-b border-black/[0.09]">
                    생성일
                  </TableHead>
                  <TableHead className="bg-black/[0.04] text-xs font-normal uppercase text-[#000000] h-10 py-0 px-3 w-[233px] border-b border-black/[0.09]">
                    상태
                  </TableHead>
                  <TableHead className="bg-black/[0.04] text-xs font-normal uppercase text-[#000000] h-10 py-0 px-3 text-right border-b border-black/[0.09]">
                    관리
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inquiries.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={4} className="py-0 text-base text-[#8D8D8D] h-[48px]">
                      문의 내역이 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  inquiries.map((inquiry) => (
                    <TableRow
                      key={inquiry.id}
                      className="last:[&_td]:border-b-0 cursor-pointer"
                      onClick={() => navigate(`/inquiries/${inquiry.id}`)}
                    >
                      <TableCell className="py-0 max-w-[444px] h-[48px] border-b border-black/[0.09]">
                        <span
                          className="block truncate text-base text-[#000000]"
                          title={inquiry.title}
                        >
                          {inquiry.title}
                        </span>
                      </TableCell>
                      <TableCell className="py-0 text-base text-[#000000] h-[48px] border-b border-black/[0.09] w-[339px]">
                        {formatDate(inquiry.created_at)}
                      </TableCell>
                      <TableCell className="py-0 h-[48px] border-b border-black/[0.09] w-[233px]">
                        {getStatusBadge(inquiry.status)}
                      </TableCell>
                      <TableCell className="py-0 px-3 h-[48px] border-b border-black/[0.09]">
                        <div
                          className="flex items-center justify-end"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            variant="ghost"
                            onClick={() => setDeleteTarget(inquiry.id)}
                            className="w-[80px] h-auto rounded bg-[#F7DDCF] px-3 py-2 text-xs font-bold text-[#000000] hover:bg-[rgba(245,43,0,0.15)] transition-colors"
                          >
                            삭제
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            classname="hidden lg:flex"
          />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        icon={<img src={IconHelp} alt="Icon Alert Circle" />}
        iconVariant="neutral"
        title="문의 삭제 확인"
        description="해당 문의를 삭제하시겠습니까?"
        primaryAction={{ label: "네", onClick: handleDelete }}
        secondaryAction={{
          label: "아니요",
          onClick: () => setDeleteTarget(null),
        }}
      />
    </div>
    </>
  );
}
